import { useState } from 'react';
import Reveal from '@/components/base/Reveal';

// Exported so the page can build matching FAQPage structured data from the
// exact same questions and answers that render on screen.
export const faqs = [
  {
    q: 'What market does the Expert Advisor trade?',
    a: 'It works on a single instrument, XAUUSD. Every part of the decision flow is built around reading gold price behaviour rather than spreading logic across a broad basket of symbols, so the process stays focused on one market.',
  },
  {
    q: 'What happens when the conditions are not met?',
    a: 'The system waits. Instead of forcing a trade, it loops back and keeps observing the market until a valid setup appears. Not taking a trade is a normal outcome of the process, not a failure of it.',
  },
  {
    q: 'How does it decide which direction to trade?',
    a: 'Direction comes from the market structure the system reads at that moment. Both long and short positions are part of the recorded behaviour, so it does not default to one side of the market.',
  },
  {
    q: 'How is a position handled after it is opened?',
    a: 'Once a trade is open it moves into the management stage and is carried through to its exit. Management sits between execution and recording in the loop, so no position is left unattended after entry.',
  },
  {
    q: 'Where does the actual execution happen?',
    a: 'Execution runs inside MetaTrader 5. The EA places and manages orders through standard MT5 automated trading, so the account it runs on must permit algorithmic trading rather than manual confirmation.',
  },
  {
    q: 'Is every part of the process recorded?',
    a: 'Yes. Recording closes the loop. Every result, whether a profit or a loss, becomes part of the record and feeds back into how the system is reviewed over time.',
  },
];

function HowFaq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="w-full bg-background-50 border-t border-background-200 px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[880px] mx-auto">
        <Reveal>
          <div className="text-center">
            <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
              PROCESS QUESTIONS
            </span>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-foreground-950 text-[28px] leading-[1.12] sm:text-[36px] lg:text-[42px]">
              How The Loop Actually Behaves.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600 max-w-[580px] mx-auto">
              The questions people usually ask about how this decision process moves from one stage
              to the next.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 rounded-lg border border-background-300 bg-background-100 overflow-hidden">
            {faqs.map((item, idx) => {
              const isOpen = open === idx;
              return (
                <div key={item.q} className="border-b border-background-200 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="group w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left cursor-pointer"
                  >
                    <span
                      className={`font-heading font-bold tracking-tight text-[15px] md:text-[16px] transition-colors duration-200 ${
                        isOpen ? 'text-primary-700' : 'text-foreground-950 group-hover:text-primary-700'
                      }`}
                    >
                      {item.q}
                    </span>
                    <span
                      className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-background-300 group-hover:border-primary-300'
                      }`}
                    >
                      <i
                        className={`${
                          isOpen ? 'ri-subtract-line text-background-50' : 'ri-add-line text-foreground-500'
                        } text-[15px]`}
                      ></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 md:px-7 pb-6 text-[14px] leading-relaxed text-foreground-600 max-w-[720px]">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default HowFaq;